export interface TilePreviewProps {
  label: string;
  color: string;
  imageUrl?: string | null;
  size?: number;
}

function initials(label: string): string {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return (words[0] ?? '?').slice(0, 2).toUpperCase();
  return `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}`.toUpperCase();
}

/**
 * WCAG relative luminance of a #rrggbb color. Used to pick a text color that
 * actually contrasts with the admin-chosen tile background — a fixed white
 * failed AA on lighter category colors (acceptance R3-F4: 4.33 on amber).
 */
function relativeLuminance(hex: string): number {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return 0;
  const channels = [0, 2, 4].map((i) => {
    const c = Number.parseInt(m[1]!.slice(i, i + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0]! + 0.7152 * channels[1]! + 0.0722 * channels[2]!;
}

function contrastText(background: string): string {
  // Threshold chosen so both candidates clear 4.5:1 on their side of it —
  // true black and true white are the two extremes, so between them every
  // possible background luminance is covered by at least one with room to
  // spare (0.179 sits inside the overlap where either choice would pass).
  //
  // This used to return '#1c130d' — this app's warm near-black, not true
  // black — for the "dark" branch. That shaves the achievable ratio just
  // enough (luminance 0.0074 instead of 0) to open a real dead zone for
  // backgrounds around luminance 0.18–0.21: neither `#1c130d` (4.2:1) nor
  // white (4.3–4.4:1) clears 4.5:1 there. Two real colors landed in exactly
  // that band — the category form's own default swatch (`#c85630`) and the
  // seeded "Postres" category (`#B5651D`) — caught by an axe-core scan
  // against the real catalog, not by a unit test with synthetic colors (see
  // docs/decisions/2026-07-31-table-page-width.md, filed alongside the
  // layout fix from the same acceptance pass). True black has no such gap.
  return relativeLuminance(background) > 0.179 ? '#000000' : '#ffffff';
}

/**
 * Renders a product or category tile exactly the way the POS will: the image
 * when one exists, and otherwise the same typographic fallback (initials on
 * the category/accent color) — never a broken image frame or a grey box.
 */
export function TilePreview({ label, color, imageUrl, size = 96 }: TilePreviewProps) {
  return (
    <div
      className="tile-preview"
      style={{ background: color, color: contrastText(color), width: size, height: size, fontSize: size * 0.32 }}
    >
      {imageUrl ? <img src={imageUrl} alt={label} /> : <span>{initials(label)}</span>}
    </div>
  );
}
